import Section from "../ui_elements/transparency_portal/Section";
import ErrorMessage from "../ui_elements/ErrorMessage";

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { getPortalSectionsAPI } from "../../services/api";

const TransparencyPortal = () => {
  const [transparencySectionData, setTransparencySectionData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchTransparencySections = async () => {
    setLoading(true);

    try {
      const response = await getPortalSectionsAPI();

      if (response && response.status === 200) {
        const { data } = response;

        setTransparencySectionData({
          sections: data
        });
      }
    } catch (error) {
      console.error(error);
      setError("Não foi possível carregar os dados da transparência.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTransparencySections()
  }, []);

  const normalizeString = str => {
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-zA-Z0-9]/g, "");
  }

  const renderTopButtons = () => {
    if (searchTerm || transparencySectionData === undefined || transparencySectionData.sections === undefined) {
      return
    }

    const topButtons = transparencySectionData.sections?.map(section => {
      return { id: section.id, name: section.title, anchor: normalizeString(section.title) }
    });

    return (
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {topButtons.map((btn) => (
          <a
            key={btn.id}
            href={`#${btn.anchor}`}
            className="flex items-center justify-center text-center bg-blue-100 text-blue-900 font-semibold py-3 px-6 rounded-lg hover:bg-blue-200 transition duration-200"
          >
            {btn.name}
          </a>
        ))}
      </div>
    )
  }

  const renderSections = () => {
    if (transparencySectionData === undefined || transparencySectionData.sections === undefined) {
      return
    }

    const normalized = searchTerm.toLowerCase();

    const sections = transparencySectionData.sections
      .map(section => ({
        ...section,
        items: searchTerm
          ? section.items.filter(item => item.title.toLowerCase().includes(normalized))
          : section.items,
      }))
      .filter(section => section.items.length > 0);

    if (searchTerm && sections.length === 0) {
      return <p className="text-gray-500 text-center py-8">Nenhum item encontrado para "{searchTerm}".</p>;
    }

    return sections.map((section) => (
      <div key={section.id} id={normalizeString(section.title)}>
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center uppercase">
          {section.title}
          <span className="flex-1 border-t border-gray-300 ml-4"></span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {section.items.map((item) => (
            <Section
              key={item.id}
              name={item.title}
              url={
                item.type === 'EXTERNAL_LINK' && item.path
                  ? `/transparencia/${item.path}`
                  : item.type === 'INTERNAL_LIST'
                    ? `/transparencia/documentos/${section.id}/${item.title}`
                    : item.type === 'EXTERNAL_FINAL_LINK'
                      ? item.externalUrl
                      : ""
              }
              icon={""}
            />
          ))}
        </div>
      </div>
    ))
  }

  if (loading) return <div className="py-8 text-center text-gray-500">Carregando dados...</div>;
  if (error) return (
    <ErrorMessage
      title="Estamos melhorando o Portal para você"
      description="No momento, as informações do Portal da Transparência estão temporariamente indisponíveis. Nossa equipe já está trabalhando para restabelecer o acesso o mais breve possível."
    />
  );

  return (
    <div className="p-8 space-y-8">
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-sm font-semibold uppercase tracking-widest text-blue-900">
          Encontre o que você precisa
        </p>
        <div className="relative w-full max-w-2xl">
          <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Pesquisar documentos e informações..."
            className="w-full border border-gray-300 rounded-xl pl-12 pr-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          />
        </div>
      </div>
      {renderTopButtons()}
      {renderSections()}

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 bg-blue-50 border border-blue-100 rounded-xl">
        <div>
          <p className="font-semibold text-blue-900">Buscando dados da gestão anterior?</p>
          <p className="text-sm text-blue-700 mt-0.5">Acesse o portal com as informações de 2021–2024.</p>
        </div>
        <Link
          to="/transparencia/anterior"
          className="shrink-0 inline-flex items-center gap-2 bg-blue-900 text-white font-semibold py-2 px-5 rounded-lg hover:bg-blue-700 transition duration-200"
        >
          Ver Gestão Anterior
        </Link>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-5 bg-gray-50 border border-gray-200 rounded-xl">
        <div>
          <p className="font-semibold text-gray-800">Dúvidas ou termos técnicos?</p>
          <p className="text-sm text-gray-500 mt-0.5">
            Acesse as Perguntas Frequentes e o Glossário do Portal da Transparência.
          </p>
        </div>
        <Link
          to="/transparencia/perguntas-e-respostas"
          className="shrink-0 inline-flex items-center gap-2 bg-blue-900 text-white font-semibold py-2 px-5 rounded-lg hover:bg-blue-700 transition duration-200"
        >
          Perguntas e Respostas
        </Link>
      </div>

      <div className="mt-12 p-6 bg-gray-100 rounded-lg">
        <p className="text-gray-700 text-lg leading-relaxed">
          Conforme a Lei Complementar Federal 131/2009, a Prefeitura de Serrinha
          disponibiliza seu Portal da Transparência visando assegurar ao cidadão
          informações sobre as execuções orçamentárias e financeiras do
          município, o acompanhamento das aplicações dos recursos públicos,
          permitindo maior fiscalização e participação.
        </p>
        <p className="mt-4 text-gray-700 text-lg">
          Para informações pessoalmente, dirija-se à unidade física do
          <strong> SIC-OUVIDORIA</strong>, localizado na Prefeitura Municipal de
          Serrinha, Rua Manoel Joaquim de Souza, 150, Centro, 59.258-000,
          Serrinha/RN no horário das <strong>08:00h às 13:00h</strong>.
        </p>
      </div>
    </div>
  );
};

export default TransparencyPortal;
